import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Testimonial } from '../../types';
import { getTestimonials } from '../../apis/testimonial';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import {
  Button,
  EmptyState,
  Section,
  SectionHeading,
  Skeleton,
  accent,
  accentOrder,
  cn, Img } from '../ui';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const { data: fetchedTestimonials, isLoading, isError } = useQuery<Testimonial[], Error>({
    queryKey: ['testimonials'],
    queryFn: () => getTestimonials(),
  });

  const testimonials = fetchedTestimonials || [];
  const count = testimonials.length;

  // Functional updates so the handlers do not close over `currentIndex`; the
  // autoplay effect below then only has to restart when the count changes,
  // instead of on every render as it did before.
  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (count === 0 ? 0 : (prevIndex + 1) % count));
  }, [count]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prevIndex) => (count === 0 ? 0 : (prevIndex - 1 + count) % count));
  }, [count]);

  // Keeps the index in range if the list shrinks between fetches.
  useEffect(() => {
    setCurrentIndex((prevIndex) => (prevIndex < count ? prevIndex : 0));
  }, [count]);

  // WCAG 2.2.2: auto-advancing content has to stop when someone is reading or
  // operating it, so hover and keyboard focus both hold the carousel still.
  useEffect(() => {
    if (count > 1 && !isPaused) {
      const timer = setInterval(() => {
        handleNext();
      }, 5000); // Change slide every 5 seconds
      return () => clearInterval(timer);
    }
  }, [count, handleNext, isPaused]);

  const heading = (
    <SectionHeading
      align="center"
      eyebrow="Testimonials"
      eyebrowAccent={accent('rose').rule}
      title="What Our Community Says"
      description="Join thousands of satisfied students and researchers"
    />
  );

  if (isLoading) {
    return (
      <Section tone="gold">
        {heading}
        <div className="mx-auto max-w-3xl overflow-hidden rounded-lg border border-border bg-background-card">
          <span aria-hidden="true" className={cn('block h-1', accent('rose').rule)} />
          <div className="space-y-4 p-6 sm:p-8">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-2/3" />
            <div className="flex items-center gap-3 pt-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          </div>
        </div>
      </Section>
    );
  }

  if (isError || count === 0) {
    return (
      <Section tone="gold">
        {heading}
        <EmptyState
          tone="rose"
          icon={<Quote size={20} aria-hidden="true" />}
          title="No testimonials yet"
          description={
            isError
              ? 'We could not load testimonials right now. Please try again later.'
              : 'Once learners share how the courses went for them, their words will appear here.'
          }
        />
      </Section>
    );
  }

  const currentTestimonial = testimonials[currentIndex];
  const rating = currentTestimonial?.rating ?? 0;

  // The card changes colour as the carousel advances, so the accent doubles as a
  // second, non-textual signal that the quote you are reading has moved on.
  const palette = accent(accentOrder[currentIndex % accentOrder.length]);

  return (
    <Section tone="gold">
      {heading}

      <div
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocusCapture={() => setIsPaused(true)}
        onBlurCapture={() => setIsPaused(false)}
      >
      <figure
        key={currentIndex}
        className="mx-auto max-w-3xl animate-fade-up overflow-hidden rounded-lg border border-border bg-background-card"
      >
        <span aria-hidden="true" className={cn('block h-1', palette.rule)} />

        <div className="p-6 sm:p-10">
          <span
            aria-hidden="true"
            className={cn('inline-flex h-10 w-10 items-center justify-center rounded-lg', palette.soft, palette.text)}
          >
            <Quote size={20} />
          </span>

          <blockquote className="mt-5 font-display text-h4 leading-relaxed text-text-primary sm:text-h3">
            &ldquo;{currentTestimonial?.text}&rdquo;
          </blockquote>

          {rating > 0 && (
            <div
              className="mt-6 flex items-center gap-0.5"
              role="img"
              aria-label={`Rated ${rating} out of 5`}
            >
              {Array.from({ length: rating }, (_, i) => (
                <Star key={i} aria-hidden="true" className="h-4 w-4 fill-current text-warning" />
              ))}
            </div>
          )}

          <figcaption className="mt-6 flex items-center gap-4 border-t border-border pt-6">
            {currentTestimonial?.avatar && (
              <Img
                src={currentTestimonial.avatar}
                width={48}
                height={48}
                className="h-12 w-12 shrink-0 rounded-full border border-border"
              />
            )}
            <div className="min-w-0">
              <p className="text-body font-semibold text-text-primary">
                {currentTestimonial?.authorName}
              </p>
              <p className="text-caption text-text-secondary">{currentTestimonial?.authorRole}</p>
            </div>
          </figcaption>
        </div>
      </figure>

      {count > 1 && (
        <div className="mx-auto mt-8 flex max-w-3xl items-center justify-center gap-4">
          <Button
            variant="secondary"
            size="icon"
            onClick={handlePrev}
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </Button>

          <div className="flex items-center gap-1">
            {testimonials.map((testimonial, index) => (
              <button
                key={testimonial._id ?? index}
                type="button"
                onClick={() => setCurrentIndex(index)}
                aria-label={`Show testimonial ${index + 1} of ${count}`}
                aria-current={currentIndex === index || undefined}
                className="flex h-11 w-6 items-center justify-center"
              >
                {/* Size as well as colour marks the current dot, so the state is
                    not carried by hue alone. */}
                <span
                  className={cn(
                    'block rounded-full transition-colors duration-200',
                    currentIndex === index ? cn('h-2.5 w-2.5', palette.rule) : 'h-1.5 w-1.5 bg-border',
                  )}
                />
              </button>
            ))}
          </div>

          <Button
            variant="secondary"
            size="icon"
            onClick={handleNext}
            aria-label="Next testimonial"
          >
            <ChevronRight size={18} aria-hidden="true" />
          </Button>
        </div>
      )}
      </div>
    </Section>
  );
};

export default TestimonialsSection;
